/**
 * SocialFacebookHistory Component
 *
 * View posts from connected Facebook Page with engagement metrics.
 * Allows importing posts into Owaza for tracking.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Facebook,
  Heart,
  MessageCircle,
  Share2,
  ExternalLink,
  Download,
  RefreshCw,
  Loader2,
  CheckCircle2,
  Image as ImageIcon,
  Video,
  FileText,
  ChevronDown,
  AlertCircle,
  ArrowLeft,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { social } from '@/services/api/admin';

const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toString() || '0';
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const MediaTypeIcon = ({ type }) => {
  switch (type) {
    case 'video':
      return <Video className="h-4 w-4" />;
    case 'photo':
    case 'album':
      return <ImageIcon className="h-4 w-4" />;
    default:
      return <FileText className="h-4 w-4" />;
  }
};

const FacebookPostCard = ({ post, onImport, isImporting }) => {
  const [showFullMessage, setShowFullMessage] = useState(false);
  const message = post.message || post.story || '';
  const isLongMessage = message.length > 280;
  const displayMessage = showFullMessage ? message : message.slice(0, 280);

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        <div className="flex gap-4">
          {/* Media Preview */}
          {post.full_picture && (
            <div className="flex-shrink-0 w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden">
              <img
                src={post.full_picture}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Header */}
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline" className="gap-1">
                <MediaTypeIcon type={post.media_type} />
                {post.media_type || 'status'}
              </Badge>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {formatDate(post.created_time)}
              </span>
              {post.is_imported && (
                <Badge className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  In Owaza
                </Badge>
              )}
            </div>

            {/* Message */}
            {message && (
              <div className="mb-3">
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                  {displayMessage}
                  {isLongMessage && !showFullMessage && '...'}
                </p>
                {isLongMessage && (
                  <button
                    onClick={() => setShowFullMessage(!showFullMessage)}
                    className="text-sm text-blue-600 dark:text-blue-400 hover:underline mt-1"
                  >
                    {showFullMessage ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
            )}

            {/* Engagement */}
            <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-1">
                <Heart className="h-4 w-4 text-red-500" />
                {formatNumber(post.engagement?.likes)}
              </div>
              <div className="flex items-center gap-1">
                <MessageCircle className="h-4 w-4 text-blue-500" />
                {formatNumber(post.engagement?.comments)}
              </div>
              <div className="flex items-center gap-1">
                <Share2 className="h-4 w-4 text-green-500" />
                {formatNumber(post.engagement?.shares)}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            {post.permalink_url && (
              <Button
                variant="outline"
                size="sm"
                asChild
              >
                <a href={post.permalink_url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            )}
            {!post.is_imported && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onImport(post)}
                disabled={isImporting}
              >
                {isImporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const LoadingSkeleton = () => (
  <div className="space-y-4">
    {[...Array(3)].map((_, i) => (
      <Card key={i}>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <Skeleton className="w-24 h-24 rounded-lg" />
            <div className="flex-1 space-y-3">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <div className="flex gap-4">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-16" />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

const SocialFacebookHistory = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [pageName, setPageName] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(true);
  const { toast } = useToast();

  const fetchPosts = async (cursor = null) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await social.getFacebookPosts({
        limit: 25,
        after: cursor,
      });
      if (cursor) {
        setPosts((prev) => [...prev, ...data.posts]);
      } else {
        setPosts(data.posts);
      }
      setPageName(data.page_name);
      setHasMore(data.has_more);
      setNextCursor(data.next_cursor);
      setIsConnected(data.connected !== false);
    } catch (err) {
      setError(err.message || 'Failed to load Facebook posts');
      if (!cursor) {
        setPosts([]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleImport = async (post) => {
    setImportingId(post.id);
    try {
      const result = await social.importFacebookPost(post.id);
      toast({
        title: result.status === 'already_imported' ? 'Already Imported' : 'Post Imported',
        description: result.status === 'already_imported'
          ? 'This post was already in your Owaza posts'
          : 'The post has been added to your Owaza posts',
      });
      // Update the post in the list to show as imported
      setPosts((prev) =>
        prev.map((p) =>
          p.id === post.id
            ? { ...p, is_imported: true, owaza_post_id: result.owaza_post_id }
            : p
        )
      );
    } catch (err) {
      toast({
        title: 'Import Failed',
        description: err.message || 'Failed to import post',
        variant: 'destructive',
      });
    } finally {
      setImportingId(null);
    }
  };

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      const result = await social.syncFacebookPosts();
      toast({
        title: 'Sync Complete',
        description: `Updated engagement for ${result.synced} of ${result.total} posts`,
      });
      // Refresh the list
      fetchPosts();
    } catch (err) {
      toast({
        title: 'Sync Failed',
        description: err.message || 'Failed to sync engagement metrics',
        variant: 'destructive',
      });
    } finally {
      setIsSyncing(false);
    }
  };

  const handleLoadMore = () => {
    if (nextCursor) {
      fetchPosts(nextCursor);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/outreach/social')}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Posts
          </Button>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
              <Facebook className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Facebook History</h2>
              {pageName && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{pageName}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={handleSync}
              disabled={isSyncing || isLoading}
            >
              {isSyncing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Sync Metrics
            </Button>
            <Button
              variant="outline"
              onClick={() => fetchPosts()}
              disabled={isLoading}
            >
              <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
            </Button>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && !isLoading && (
        <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <div>
              <p className="font-medium text-red-700 dark:text-red-300">
                Could not load Facebook posts
              </p>
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Posts List */}
      {isLoading && posts.length === 0 ? (
        <LoadingSkeleton />
      ) : !isConnected && !error ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Facebook className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              No Facebook Page connected
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Connect your Facebook Page to view and import its posts.
            </p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate('/admin/outreach/social/platforms')}
            >
              Connect Facebook Page
            </Button>
          </CardContent>
        </Card>
      ) : posts.length === 0 && !error ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Facebook className="h-12 w-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              No posts found
            </h3>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              Your Facebook Page doesn't have any posts yet.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {posts.map((post) => (
            <FacebookPostCard
              key={post.id}
              post={post}
              onImport={handleImport}
              isImporting={importingId === post.id}
            />
          ))}

          {/* Load More */}
          {hasMore && (
            <div className="text-center pt-4">
              <Button
                variant="outline"
                onClick={handleLoadMore}
                disabled={isLoading}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <ChevronDown className="h-4 w-4 mr-2" />
                )}
                Load More Posts
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SocialFacebookHistory;
